import React, { useEffect, useState } from "react";
import moment from "moment";
import {
    fetchDeletionRequests,
    processDeletionRequest,
} from "../../actions/deletionRequests";
import { Container, Row, Col, Table, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getUserFromLocalStorage } from "utilities/user";
import { toast } from "react-toastify";
import { AppConfirmDialog } from "../../components/ui";

// ─── Power BI palette ─────────────────────────────────────────────────────────
const PBI = {
    bg: "#f3f2f1",
    surface: "#ffffff",
    headerBg: "#252423",
    headerText: "#ffffff",
    accent: "#e8563a",
    accentSoft: "#fdf0ed",
    blue: "#118dff",
    blueSoft: "#e8f4ff",
    green: "#12b76a",
    greenSoft: "#e6f9f0",
    amber: "#f79009",
    amberSoft: "#fff8ec",
    grey: "#605e5c",
    greyLight: "#edebe9",
    border: "#d2d0ce",
    textPrimary: "#252423",
    textSecondary: "#605e5c",
    textMuted: "#a19f9d",
};

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, color, softColor, icon }) => (
    <div
        style={{
            background: PBI.surface,
            border: `1px solid ${PBI.border}`,
            borderTop: `3px solid ${color}`,
            borderRadius: "4px",
            padding: "18px 20px",
            minWidth: "160px",
            flex: "1",
        }}
    >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600", color: PBI.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {label}
            </span>
            <div style={{ width: "28px", height: "28px", borderRadius: "4px", background: softColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={icon} style={{ fontSize: "12px", color }} />
            </div>
        </div>
        <div style={{ fontSize: "28px", fontWeight: "700", color: PBI.textPrimary, lineHeight: 1 }}>
            {value}
        </div>
    </div>
);

// ─── Status chip ──────────────────────────────────────────────────────────────
const statusConfig = {
    PENDING:   { bg: PBI.amberSoft, color: PBI.amber,  border: "#fddfa2", dot: PBI.amber  },
    COMPLETED: { bg: PBI.greenSoft, color: PBI.green,  border: "#a3e9c8", dot: PBI.green  },
    REJECTED:  { bg: PBI.greyLight, color: PBI.grey,   border: PBI.border, dot: PBI.grey  },
};

const StatusChip = ({ status }) => {
    const cfg = statusConfig[status] || statusConfig.REJECTED;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
            borderRadius: "12px", padding: "3px 9px", fontSize: "11px", fontWeight: "700",
            letterSpacing: "0.3px", textTransform: "uppercase", whiteSpace: "nowrap",
        }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: cfg.dot, flexShrink: 0 }} />
            {status}
        </span>
    );
};

// ─── Platform tag ─────────────────────────────────────────────────────────────
const PlatformTag = ({ platform }) => (
    <span style={{
        background: PBI.blueSoft, color: PBI.blue, border: `1px solid #b3d9ff`,
        borderRadius: "3px", padding: "2px 7px", fontSize: "10px", fontWeight: "700",
        letterSpacing: "0.4px", textTransform: "uppercase",
    }}>
        {platform || "—"}
    </span>
);

// ─── Account tag ──────────────────────────────────────────────────────────────
// Customers, riders and vendors are separate collections, so "delete this
// account" means a different record in each. Process the row against the one
// named here, not the one the email address suggests.
const ACCOUNT_LABELS = { customer: "Customer", rider: "Rider", vendor: "Vendor" };
const ACCOUNT_TONES = {
    customer: { bg: PBI.blueSoft, fg: PBI.blue, border: "#b3d9ff" },
    rider: { bg: "#fff4e5", fg: "#8a5000", border: "#ffd9a0" },
    vendor: { bg: "#efe7fb", fg: "#5b3ba8", border: "#d6c4f2" },
};
const AccountTag = ({ accountType }) => {
    const key = ACCOUNT_LABELS[accountType] ? accountType : "customer";
    const tone = ACCOUNT_TONES[key];
    return (
        <span style={{
            background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
            borderRadius: "3px", padding: "2px 7px", fontSize: "10px", fontWeight: "700",
            letterSpacing: "0.4px", textTransform: "uppercase",
        }}>
            {ACCOUNT_LABELS[key]}
        </span>
    );
};

// ─── Confirmation Modal ───────────────────────────────────────────────────────
const ConfirmModal = ({ req, onConfirm, onCancel }) => (
    <AppConfirmDialog
        open
        title="Process this deletion request?"
        consequence={
            <>
                This <strong>deactivates</strong> the account for <strong>{req?.email || req?.phoneNumber || "this user"}</strong>.
                Their profile is disabled and the request is marked <strong>COMPLETED</strong>. Financial records are kept.
            </>
        }
        confirmLabel="Mark as processed"
        tone="danger"
        onConfirm={onConfirm}
        onClose={onCancel}
    >
        <div className="lk-note lk-note--warning" style={{ marginTop: 12 }}>
            <i className="fas fa-exclamation-triangle" aria-hidden="true" style={{ marginTop: 2 }} />
            <span>This cannot be undone. Make sure the request is genuine before you continue.</span>
        </div>
    </AppConfirmDialog>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const DeletionRequests = () => {
    const dispatch = useDispatch();
    const user = getUserFromLocalStorage();

    const { requests, isLoading } = useSelector(
        (state) => state.deletionRequests
    );

    const [pendingRequest, setPendingRequest] = useState(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    useEffect(() => {
        dispatch(fetchDeletionRequests());
    }, []);

    // Guard — Super Admin / Admin only
    if (
        user?.result?.user_role !== "SUPERUSER" &&
        user?.result?.user_role !== "SUPER-ADMIN" &&
        user?.result?.user_role !== "ADMIN"
    ) {
        return (
            <div style={{ padding: "60px 30px", textAlign: "center" }}>
                <div style={{
                    display: "inline-flex", flexDirection: "column", alignItems: "center",
                    gap: "14px", padding: "40px 48px", background: PBI.surface,
                    border: `1px solid ${PBI.border}`, borderTop: `3px solid ${PBI.accent}`, borderRadius: "4px",
                }}>
                    <i className="fas fa-lock" style={{ fontSize: "32px", color: PBI.accent }} />
                    <span style={{ fontSize: "16px", fontWeight: "700", color: PBI.textPrimary }}>Restricted — Admins Only</span>
                    <span style={{ fontSize: "13px", color: PBI.textSecondary }}>You do not have permission to view this page.</span>
                </div>
            </div>
        );
    }

    const handleProcess = () => {
        dispatch(
            processDeletionRequest(pendingRequest._id, "deactivate", user?.result?._id)
        )
            .then(() => dispatch(fetchDeletionRequests()))
            .catch((err) => toast.error(err?.message || "Failed to process request"));
        setPendingRequest(null);
    };

    const totalCount     = requests?.length || 0;
    const pendingCount   = requests?.filter((r) => r.status === "PENDING").length   || 0;
    const completedCount = requests?.filter((r) => r.status === "COMPLETED").length || 0;
    const rejectedCount  = requests?.filter((r) => r.status === "REJECTED").length  || 0;

    const filteredRequests = (requests || []).filter((r) => {
        const matchStatus = statusFilter === "ALL" || r.status === statusFilter;
        if (!search.trim()) return matchStatus;
        const q = search.toLowerCase();
        return matchStatus && (
            (r.name || "").toLowerCase().includes(q) ||
            (r.email || "").toLowerCase().includes(q) ||
            (r.phoneNumber || "").toLowerCase().includes(q) ||
            (r.platform || "").toLowerCase().includes(q) ||
            (r.accountType || "").toLowerCase().includes(q) ||
            (r.reason || "").toLowerCase().includes(q)
        );
    });

    const thStyle = {
        background: PBI.headerBg, color: PBI.headerText,
        fontSize: "10px", fontWeight: "700", textTransform: "uppercase",
        letterSpacing: "0.5px", padding: "11px 14px", whiteSpace: "nowrap",
        borderBottom: "2px solid #3b3a39", borderRight: "1px solid #3b3a39",
    };
    const tdStyle = {
        verticalAlign: "middle", padding: "11px 14px",
        fontSize: "13px", color: PBI.textPrimary,
        borderBottom: `1px solid ${PBI.greyLight}`,
    };

    const FILTERS = ["ALL", "PENDING", "COMPLETED", "REJECTED"];
    const filterColors = { ALL: PBI.blue, PENDING: PBI.amber, COMPLETED: PBI.green, REJECTED: PBI.grey };

    return (
        <>
            <div style={{ padding: "24px 28px", background: PBI.bg, minHeight: "100vh" }}>

                {/* ── Page header ──────────────────────────────────────────────── */}
                <div style={{
                    background: PBI.headerBg, borderRadius: "4px",
                    padding: "20px 28px", marginBottom: "20px",
                    display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <div style={{ width: "40px", height: "40px", background: PBI.accent, borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className="fas fa-user-slash" style={{ color: "#fff", fontSize: "16px" }} />
                        </div>
                        <div>
                            <div style={{ color: PBI.headerText, fontSize: "18px", fontWeight: "700", lineHeight: 1.2 }}>
                                Account Deletion Requests
                            </div>
                            <div style={{ color: PBI.textMuted, fontSize: "12px", marginTop: "3px" }}>
                                Process within <span style={{ color: "#ffa500", fontWeight: "600" }}>30 days</span> · GDPR / Play Store policy
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => dispatch(fetchDeletionRequests())}
                        style={{
                            background: "transparent", border: `1px solid #605e5c`,
                            color: PBI.textMuted, borderRadius: "3px", padding: "6px 14px",
                            fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
                        }}
                    >
                        <i className="fas fa-sync-alt" style={{ fontSize: "11px" }} />
                        Refresh
                    </button>
                </div>

                {/* ── KPI cards ────────────────────────────────────────────────── */}
                <div style={{ display: "flex", gap: "14px", marginBottom: "20px", flexWrap: "wrap" }}>
                    <KpiCard label="Total Requests" value={totalCount}     color={PBI.blue}  softColor={PBI.blueSoft}  icon="fas fa-list" />
                    <KpiCard label="Pending"         value={pendingCount}   color={PBI.amber} softColor={PBI.amberSoft} icon="fas fa-clock" />
                    <KpiCard label="Completed"        value={completedCount} color={PBI.green} softColor={PBI.greenSoft} icon="fas fa-check-circle" />
                    <KpiCard label="Rejected"         value={rejectedCount}  color={PBI.grey}  softColor={PBI.greyLight} icon="fas fa-times-circle" />
                </div>

                {/* ── Main card ────────────────────────────────────────────────── */}
                <div style={{ background: PBI.surface, border: `1px solid ${PBI.border}`, borderRadius: "4px", overflow: "hidden" }}>

                    {/* Toolbar */}
                    <div style={{
                        padding: "14px 20px", borderBottom: `1px solid ${PBI.greyLight}`,
                        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px",
                    }}>
                        {/* Status filter pills */}
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            {FILTERS.map((f) => (
                                <button
                                    key={f}
                                    onClick={() => setStatusFilter(f)}
                                    style={{
                                        padding: "5px 13px", borderRadius: "12px", fontSize: "11px", fontWeight: "700",
                                        cursor: "pointer", letterSpacing: "0.3px", textTransform: "uppercase",
                                        border: statusFilter === f ? "none" : `1px solid ${PBI.border}`,
                                        background: statusFilter === f ? filterColors[f] : PBI.surface,
                                        color: statusFilter === f ? "#fff" : PBI.textSecondary,
                                        transition: "all 0.15s",
                                    }}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div style={{ position: "relative", minWidth: "240px" }}>
                            <i className="fas fa-search" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: PBI.textMuted, fontSize: "12px", pointerEvents: "none" }} />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search name, email, phone..."
                                style={{
                                    paddingLeft: "30px", paddingRight: search ? "28px" : "10px",
                                    paddingTop: "7px", paddingBottom: "7px",
                                    border: `1px solid ${PBI.border}`, borderRadius: "3px",
                                    fontSize: "12px", width: "100%", outline: "none",
                                    color: PBI.textPrimary, background: PBI.bg,
                                }}
                            />
                            {search && (
                                <span
                                    onClick={() => setSearch("")}
                                    style={{ position: "absolute", right: "9px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: PBI.textMuted, fontSize: "13px" }}
                                >
                                    ✕
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Result count */}
                    {(search || statusFilter !== "ALL") && (
                        <div style={{ padding: "7px 20px", background: PBI.blueSoft, borderBottom: `1px solid #b3d9ff`, fontSize: "12px", color: PBI.blue }}>
                            <i className="fas fa-filter" style={{ marginRight: "6px" }} />
                            Showing <strong>{filteredRequests.length}</strong> of <strong>{totalCount}</strong> requests
                        </div>
                    )}

                    {/* Table */}
                    {isLoading ? (
                        <div style={{ textAlign: "center", padding: "60px" }}>
                            <Spinner animation="border" style={{ color: PBI.accent, width: "36px", height: "36px" }} />
                            <div style={{ marginTop: "12px", color: PBI.textSecondary, fontSize: "13px" }}>Loading requests…</div>
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <Table style={{ marginBottom: 0, width: "100%" }}>
                                <thead>
                                    <tr>
                                        {["#", "Account", "Name", "Email", "Phone", "Platform", "Reason", "Requested", "Status", "Action"].map((h, i) => (
                                            <th key={h} style={{ ...thStyle, borderRight: i === 9 ? "none" : undefined }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRequests.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} style={{ textAlign: "center", padding: "52px 20px", color: PBI.textSecondary }}>
                                                <i className="fas fa-check-circle" style={{ fontSize: "24px", color: PBI.green, display: "block", marginBottom: "10px" }} />
                                                <span style={{ fontSize: "14px", fontWeight: "600" }}>
                                                    {search || statusFilter !== "ALL" ? "No requests match your filters" : "No deletion requests — all clear!"}
                                                </span>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRequests.map((req, index) => (
                                            <tr
                                                key={req._id}
                                                style={{ background: index % 2 === 0 ? PBI.surface : "#faf9f8" }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = PBI.greyLight; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = index % 2 === 0 ? PBI.surface : "#faf9f8"; }}
                                            >
                                                <td style={{ ...tdStyle, color: PBI.textMuted, fontSize: "12px", width: "36px", textAlign: "center" }}>
                                                    {index + 1}
                                                </td>

                                                <td style={{ ...tdStyle, textAlign: "center", minWidth: "90px" }}>
                                                    <AccountTag accountType={req.accountType} />
                                                </td>

                                                <td style={{ ...tdStyle, fontWeight: "600", minWidth: "110px" }}>
                                                    {req.name || <em style={{ color: PBI.textMuted, fontStyle: "normal" }}>—</em>}
                                                </td>

                                                <td style={{ ...tdStyle, wordBreak: "break-all", minWidth: "170px", color: PBI.blue }}>
                                                    {req.email || <em style={{ color: PBI.textMuted, fontStyle: "normal" }}>—</em>}
                                                </td>

                                                <td style={{ ...tdStyle, minWidth: "120px" }}>
                                                    {req.phoneNumber || <em style={{ color: PBI.textMuted, fontStyle: "normal" }}>—</em>}
                                                </td>

                                                <td style={{ ...tdStyle, textAlign: "center", minWidth: "80px" }}>
                                                    <PlatformTag platform={req.platform} />
                                                </td>

                                                <td
                                                    style={{ ...tdStyle, maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: PBI.textSecondary }}
                                                    title={req.reason || ""}
                                                >
                                                    {req.reason || <em style={{ color: PBI.textMuted, fontStyle: "normal" }}>No reason given</em>}
                                                </td>

                                                <td style={{ ...tdStyle, whiteSpace: "nowrap", minWidth: "130px" }}>
                                                    <div style={{ fontSize: "13px", fontWeight: "600" }}>
                                                        {moment(req.requestedAt).format("DD MMM YYYY")}
                                                    </div>
                                                    <div style={{ fontSize: "11px", color: PBI.textMuted, marginTop: "2px" }}>
                                                        {moment(req.requestedAt).format("HH:mm")} · {moment(req.requestedAt).fromNow()}
                                                    </div>
                                                </td>

                                                <td style={{ ...tdStyle, textAlign: "center", minWidth: "110px" }}>
                                                    <StatusChip status={req.status} />
                                                </td>

                                                <td style={{ ...tdStyle, textAlign: "center", minWidth: "140px" }}>
                                                    {req.status === "PENDING" ? (
                                                        <button
                                                            onClick={() => setPendingRequest(req)}
                                                            style={{
                                                                padding: "6px 14px", borderRadius: "3px", border: "none",
                                                                background: PBI.accent, color: "#fff",
                                                                fontSize: "11px", fontWeight: "700", cursor: "pointer",
                                                                display: "inline-flex", alignItems: "center", gap: "5px",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            <i className="fas fa-check" style={{ fontSize: "10px" }} />
                                                            Mark Processed
                                                        </button>
                                                    ) : (
                                                        <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: PBI.green, fontSize: "12px", fontWeight: "600" }}>
                                                            <i className="fas fa-check-circle" />
                                                            Processed
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Footer */}
                    {!isLoading && filteredRequests.length > 0 && (
                        <div style={{ padding: "10px 20px", borderTop: `1px solid ${PBI.greyLight}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: "12px", color: PBI.textMuted }}>
                                {filteredRequests.length} record{filteredRequests.length !== 1 ? "s" : ""}
                            </span>
                            {pendingCount > 0 && (
                                <span style={{ fontSize: "12px", color: PBI.amber, fontWeight: "600", display: "flex", alignItems: "center", gap: "5px" }}>
                                    <i className="fas fa-clock" style={{ fontSize: "11px" }} />
                                    {pendingCount} pending action{pendingCount !== 1 ? "s" : ""}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Confirmation Modal ────────────────────────────────────────────── */}
            {pendingRequest && (
                <ConfirmModal
                    req={pendingRequest}
                    onConfirm={handleProcess}
                    onCancel={() => setPendingRequest(null)}
                />
            )}
        </>
    );
};

export default DeletionRequests;
